import React from 'react';
import type { ErrorResponse } from '@/api/handlers/responses';
import type { AttributeType, PendingAttributeValue } from '@/api/models';
import {
    useAttributeTypesQuery,
    useDeleteAttributeTypeMutation,
    usePendingAttributeValuesQuery,
    usePromoteAttributeValueMutation,
    useUpdateAttributeTypeMutation,
} from '@/api/queries';
import { useCreateAttributeType } from './useCreateAttributeType';
import type { AttributeTypeFormValues } from './components/attributeTypeFormModal/useAttributeTypeForm';
import type {
    AttributeDictionaryTab,
    AttributeTypeRow,
} from './components/attributesMeta';

/**
 * Drives the attributes page: the Types tab (list + CRUD), the Pending tab
 * (promote), and selection of a type for the values drawer. Value/alias editing
 * lives in `useAttributeValuesSheet`; both read the same `useAttributeTypesQuery`,
 * so mutations there reflect here on refetch.
 */
export const useAttributesPage = () => {
    const typesQuery = useAttributeTypesQuery();
    const pendingQuery = usePendingAttributeValuesQuery();
    const { create, isCreating } = useCreateAttributeType();
    const updateMutation = useUpdateAttributeTypeMutation();
    const deleteMutation = useDeleteAttributeTypeMutation();
    const promoteMutation = usePromoteAttributeValueMutation();

    const [tab, setTab] = React.useState<AttributeDictionaryTab>('types');
    const [search, setSearch] = React.useState('');
    const [isTypeFormOpen, setIsTypeFormOpen] = React.useState(false);
    const [editingTypeId, setEditingTypeId] = React.useState<string | null>(
        null,
    );
    const [typeFormError, setTypeFormError] = React.useState<string | null>(
        null,
    );
    const [selectedTypeId, setSelectedTypeId] = React.useState<string | null>(
        null,
    );

    const rawTypes: AttributeType[] | null = React.useMemo(() => {
        if (!typesQuery.data || !typesQuery.data.success) {
            return null;
        }
        return typesQuery.data.response.items;
    }, [typesQuery.data]);

    const types = React.useMemo<AttributeTypeRow[] | null>(() => {
        if (rawTypes === null) {
            return null;
        }
        const q = search.trim().toLowerCase();
        if (!q) {
            return rawTypes;
        }
        return rawTypes.filter(
            (type) =>
                type.name.toLowerCase().includes(q) ||
                type.slug.toLowerCase().includes(q),
        );
    }, [rawTypes, search]);

    const error: ErrorResponse | null =
        typesQuery.data && !typesQuery.data.success
            ? typesQuery.data.error
            : null;

    const pending: PendingAttributeValue[] | null = React.useMemo(() => {
        if (!pendingQuery.data || !pendingQuery.data.success) {
            return null;
        }
        return pendingQuery.data.response.items;
    }, [pendingQuery.data]);

    const pendingError: ErrorResponse | null =
        pendingQuery.data && !pendingQuery.data.success
            ? pendingQuery.data.error
            : null;

    const editingType = React.useMemo(() => {
        if (editingTypeId === null || rawTypes === null) {
            return null;
        }
        return rawTypes.find((type) => type.id === editingTypeId) ?? null;
    }, [editingTypeId, rawTypes]);

    const typeFormInitialValues: AttributeTypeFormValues | null =
        React.useMemo(() => {
            if (editingType === null) {
                return null;
            }
            return {
                name: editingType.name,
                slug: editingType.slug,
                inputKind: editingType.inputKind,
                isMultiSelect: editingType.isMultiSelect,
            };
        }, [editingType]);

    // The drawer's type is derived from live data so value/alias edits (which
    // invalidate the list) flow straight back into it.
    const selectedType = React.useMemo<AttributeType | null>(() => {
        if (selectedTypeId === null || rawTypes === null) {
            return null;
        }
        return rawTypes.find((type) => type.id === selectedTypeId) ?? null;
    }, [rawTypes, selectedTypeId]);

    const onTabChange = React.useCallback((next: AttributeDictionaryTab) => {
        setTab(next);
    }, []);

    const onSearchChange = React.useCallback((value: string) => {
        setSearch(value);
    }, []);

    const onAddType = React.useCallback(() => {
        setEditingTypeId(null);
        setTypeFormError(null);
        setIsTypeFormOpen(true);
    }, []);

    const onEditType = React.useCallback((id: string) => {
        setEditingTypeId(id);
        setTypeFormError(null);
        setIsTypeFormOpen(true);
    }, []);

    const onCloseTypeForm = React.useCallback(() => {
        setIsTypeFormOpen(false);
        setEditingTypeId(null);
        setTypeFormError(null);
    }, []);

    const onSubmitType = React.useCallback(
        async (input: AttributeTypeFormValues) => {
            setTypeFormError(null);
            if (editingTypeId === null) {
                // Shared with the Control Center's "Add attribute type", which
                // opens this same form from the launcher — see
                // `useCreateAttributeType`.
                const created = await create(input);
                if (!created.ok) {
                    setTypeFormError(created.message);
                    return;
                }
            } else {
                const result = await updateMutation.mutateAsync({
                    id: editingTypeId,
                    body: input,
                });
                if (!result.success) {
                    setTypeFormError(
                        result.error.message ??
                            'Failed to save attribute type.',
                    );
                    return;
                }
            }
            setIsTypeFormOpen(false);
            setEditingTypeId(null);
        },
        [create, editingTypeId, updateMutation],
    );

    const onDeleteType = React.useCallback(
        (id: string) => {
            deleteMutation.mutate(id);
            // If the open drawer's type was deleted, close it.
            setSelectedTypeId((current) => (current === id ? null : current));
        },
        [deleteMutation],
    );

    const onManageValues = React.useCallback((id: string) => {
        setSelectedTypeId(id);
    }, []);

    const onCloseValuesSheet = React.useCallback(() => {
        setSelectedTypeId(null);
    }, []);

    const onPromoteValue = React.useCallback(
        (typeId: string, valueId: string) => {
            promoteMutation.mutate({ typeId, valueId });
        },
        [promoteMutation],
    );

    const onRetry = React.useCallback(() => {
        typesQuery.refetch();
    }, [typesQuery]);

    const onRetryPending = React.useCallback(() => {
        pendingQuery.refetch();
    }, [pendingQuery]);

    return {
        tab,
        onTabChange,
        // types tab
        types,
        isLoading: typesQuery.isLoading,
        error,
        onRetry,
        search,
        onSearchChange,
        // type CRUD
        isTypeFormOpen,
        typeFormMode:
            editingTypeId === null ? ('create' as const) : ('edit' as const),
        typeFormInitialValues,
        typeFormError,
        isTypeSubmitting: isCreating || updateMutation.isPending,
        onAddType,
        onEditType,
        onCloseTypeForm,
        onSubmitType,
        onDeleteType,
        // values drawer
        selectedType,
        isValuesSheetOpen: selectedType !== null,
        onManageValues,
        onCloseValuesSheet,
        // pending tab
        pending,
        isPendingLoading: pendingQuery.isLoading,
        pendingError,
        onRetryPending,
        onPromoteValue,
    };
};
